
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PortalBackground from './components/PortalBackground';
import EnergyConversionBox from './components/EnergyConversionBox';
import ResonantEnergyBalloon from './components/ResonantEnergyBalloon';
import HemiSyncAnimation from './components/HemiSyncAnimation';
import UpgradeModal from './components/UpgradeModal';
import SessionTimer from './components/SessionTimer';
import { FocusState, StateConfig, Language } from './types';
import { APP_MODES, TRANSLATIONS, getLocalizedFocusConfigs } from './constants';
import { audioEngine } from './services/audioEngine';

const CIA_DOC_URL = "https://www.cia.gov/readingroom/docs/cia-rdp96-00788r001700210016-5.pdf";

const App: React.FC = () => {
  const [language, setLanguage] = useState<Language>(Language.EN);
  const [appMode, setAppMode] = useState<string>(APP_MODES.LANDING);
  const [activeFocus, setActiveFocus] = useState<FocusState>(FocusState.OFF);
  const [unlockedLevels, setUnlockedLevels] = useState<FocusState[]>(() => {
    try {
      const saved = localStorage.getItem('gateway_unlocked_levels');
      return saved ? JSON.parse(saved) : [FocusState.FOCUS_10];
    } catch (e) {
      console.warn("Failed to access localStorage:", e);
      return [FocusState.FOCUS_10];
    }
  });

  useEffect(() => {
    console.log("Gateway App Initialized");
  }, []);
  const [activeTab, setActiveTab] = useState<'session' | 'tools'>('tools');
  const [activeTool, setActiveTool] = useState<'box' | 'rebal'>('box');
  const [volume, setVolume] = useState(0.5);
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);
  const [isOBEModalOpen, setIsOBEModalOpen] = useState(false);
  const [isShifting, setIsShifting] = useState(false);
  const [manualTab, setManualTab] = useState<'info' | 'tutorial'>('info');
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);
  const [instructionFocus, setInstructionFocus] = useState<StateConfig | null>(null);
  
  const [vibeActive, setVibeActive] = useState(false);
  const [vibeTimeLeft, setVibeTimeLeft] = useState(0);
  const [vibeDurationSetting, setVibeDurationSetting] = useState(120); 


  const localizedConfigs = getLocalizedFocusConfigs(language);
  const t = TRANSLATIONS[language];

  const [isAutoProtocol, setIsAutoProtocol] = useState(false);
  const [isSessionActive, setIsSessionActive] = useState(false);
  
  const [timeLeft, setTimeLeft] = useState(1200); 
  const [totalDuration, setTotalDuration] = useState(1200);

  const activeGuideRef = useRef<AudioBufferSourceNode | null>(null);
  const timerRef = useRef<number | null>(null);
  const vibeTimerRef = useRef<number | null>(null);

  const hapticFeedback = (type: 'light' | 'medium' | 'heavy' = 'light') => {
    if ('vibrate' in navigator) {
      const patterns = {
        light: [10],
        medium: [20],
        heavy: [50]
      };
      navigator.vibrate(patterns[type]);
    }
  };

  const handleInitialize = async (selectedLang: Language = Language.EN) => {
    setLanguage(selectedLang);
    localStorage.setItem('gateway_language', selectedLang);
    
    hapticFeedback('medium');
    await audioEngine.initialize();
    setAppMode(APP_MODES.APP);
    setActiveTab('session');
    
    // Auto-start Focus 10 with the selected language
    const configs = getLocalizedFocusConfigs(selectedLang);
    const focus10Config = configs.find(c => c.id === FocusState.FOCUS_10) || configs[0];
    handleFocusChange(focus10Config);
  };

  const stopAllAudio = useCallback(() => {
    setIsSessionActive(false);
    setIsAutoProtocol(false);
    audioEngine.stopBinaural();
    audioEngine.setVolume(0);
    setVolume(0); 
    if (activeGuideRef.current) {
      activeGuideRef.current.stop();
      activeGuideRef.current = null;
    }
  }, []);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const handleFocusChange = useCallback((config: StateConfig, isAutopilotStart = false) => {
    const isUnlocked = unlockedLevels.includes(config.id);
    
    if (!isUnlocked) {
      return;
    }

    // If it's not an autopilot start, allow toggling off the current focus
    if (!isAutopilotStart && config.id === activeFocus && isSessionActive && !isAutoProtocol) {
      stopAllAudio();
      return;
    }
    
    if (config.id !== activeFocus && activeFocus !== FocusState.OFF) {
      hapticFeedback('medium');
      setIsShifting(true);
      audioEngine.playShiftCue();
      setTimeout(() => setIsShifting(false), 1800);
    } else {
      hapticFeedback('light');
    }

    setActiveFocus(config.id);
    setTimeLeft(config.durationSeconds);
    setTotalDuration(config.durationSeconds);
    
    setIsSessionActive(true);
    const newVolume = volume === 0 ? 0.5 : volume;
    if (volume === 0) setVolume(0.5);
    audioEngine.setVolume(newVolume);
    audioEngine.setBinaural(config.baseFreq, config.binauralFreq, config.resonance);
  }, [unlockedLevels, activeFocus, isSessionActive, isAutoProtocol, stopAllAudio, volume]);

  const stopVibration = useCallback(() => {
    setVibeActive(false);
    setVibeTimeLeft(0);
    audioEngine.setVibrationalState(false);
    if (vibeTimerRef.current) clearInterval(vibeTimerRef.current);
  }, []);

  const onVibrationEnd = useCallback(() => {
    setVibeActive(false);
    if (vibeTimerRef.current) clearInterval(vibeTimerRef.current);
    audioEngine.setVibrationalState(false);
    audioEngine.setVolume(0);
    setVolume(0);
  }, []);

  const handleVibrationToggle = useCallback(() => {
    hapticFeedback('heavy');
    if (!vibeActive) {
      setVibeActive(true);
      setVibeTimeLeft(vibeDurationSetting);
      const newVolume = volume === 0 ? 0.5 : volume;
      if (volume === 0) setVolume(0.5);
      audioEngine.setVolume(newVolume);
      audioEngine.setVibrationalState(true);
      vibeTimerRef.current = window.setInterval(() => {
        setVibeTimeLeft(prev => {
          if (prev <= 1) {
            onVibrationEnd();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      stopVibration();
    }
  }, [vibeActive, vibeDurationSetting, volume, onVibrationEnd, stopVibration]);

  const advanceProtocol = useCallback(() => {
    const currentIndex = localizedConfigs.findIndex(c => c.id === activeFocus);
    if (currentIndex < localizedConfigs.length - 1) {
      const nextConfig = localizedConfigs[currentIndex + 1];
      handleFocusChange(nextConfig);
    } else if (currentIndex === localizedConfigs.length - 1 && isAutoProtocol) {
      // Start OBE protocol for 1 minute automatically after Focus 21
      setVibeDurationSetting(60);
      handleVibrationToggle();
    } else {
      stopAllAudio();
    }
  }, [localizedConfigs, activeFocus, isAutoProtocol, handleFocusChange, handleVibrationToggle, stopAllAudio]);

  useEffect(() => {
    if (isSessionActive && timeLeft > 0) {
      timerRef.current = window.setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && isSessionActive) {
      // Unlock next level on session completion
      const currentIndex = localizedConfigs.findIndex(c => c.id === activeFocus);
      if (currentIndex !== -1 && currentIndex < localizedConfigs.length - 1) {
        const nextLevel = localizedConfigs[currentIndex + 1].id;
        if (!unlockedLevels.includes(nextLevel)) {
          const newUnlocked = [...unlockedLevels, nextLevel];
          // Use setTimeout to avoid synchronous state update in effect warning
          setTimeout(() => {
            setUnlockedLevels(newUnlocked);
            localStorage.setItem('gateway_unlocked_levels', JSON.stringify(newUnlocked));
          }, 0);
        }
      }

      audioEngine.setVolume(0);
      setTimeout(() => {
        setVolume(0);
        if (isAutoProtocol) advanceProtocol();
        else stopAllAudio();
      }, 0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isSessionActive, timeLeft, isAutoProtocol, activeFocus, unlockedLevels, localizedConfigs, advanceProtocol, stopAllAudio]);

  const toggleSession = () => {
    if (isSessionActive) stopAllAudio();
    else {
      const config = localizedConfigs.find(c => c.id === activeFocus) || localizedConfigs[0];
      handleFocusChange(config);
    }
  };

  const handleAutoPilotToggle = () => {
    const newAutoPilotState = !isAutoProtocol;
    setIsAutoProtocol(newAutoPilotState);
    if (newAutoPilotState) {
      const firstConfig = localizedConfigs[0];
      if (activeGuideRef.current) {
        activeGuideRef.current.stop();
        activeGuideRef.current = null;
      }
      // Pass true to handleFocusChange to indicate it's an autopilot start
      handleFocusChange(firstConfig, true);
    }
  };

  const generateContinuousWavePath = (freq: number, phaseShift: number = 0) => {
    const width = 200;
    const height = 24;
    const midY = height / 2;
    const points = 120;
    const step = width / points;
    const cycles = Math.max(1, Math.round(freq * 1.5)); 
    let path = `M 0 ${midY}`;
    for (let i = 0; i <= points; i++) {
      const x = i * step;
      const angle = (x / 100) * Math.PI * 2 * cycles + phaseShift;
      const y = midY + Math.sin(angle) * (midY - 4);
      path += ` L ${x} ${y}`;
    }
    return path;
  };

  const handleLanguageSelect = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('gateway_language', lang);
    setAppMode(APP_MODES.LANDING);
    hapticFeedback('medium');
  };

  const currentConfig = localizedConfigs.find(c => c.id === activeFocus);


  if (appMode === APP_MODES.LANGUAGE) {
    return (
      <div className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden bg-[#020617] text-center p-6 select-none">
        <PortalBackground />
        <div className="z-10 relative flex flex-col items-center gap-12 w-full max-w-md">
          <div className="flex flex-col items-center gap-4">
            <h1 className="text-4xl md:text-6xl font-black tracking-tighter text-white uppercase italic">SELECT<br/>LANGUAGE</h1>
            <div className="h-1 w-20 bg-blue-500/50 rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 gap-4 w-full">
            <button 
              onClick={() => handleLanguageSelect(Language.EN)}
              className="group relative w-full py-6 bg-blue-600/20 hover:bg-blue-600/40 border border-blue-500/30 rounded-2xl font-black text-xl md:text-2xl transition-all active:scale-95 flex items-center justify-center gap-4"
            >
              <span className="text-2xl">🇺🇸</span>
              <span className="text-white tracking-widest">ENGLISH</span>
            </button>
            
            <button 
              onClick={() => handleLanguageSelect(Language.IT)}
              className="group relative w-full py-6 bg-green-600/20 hover:bg-green-600/40 border border-green-500/30 rounded-2xl font-black text-xl md:text-2xl transition-all active:scale-95 flex items-center justify-center gap-4"
            >
              <span className="text-2xl">🇮🇹</span>
              <span className="text-white tracking-widest">ITALIANO</span>
            </button>

            <button 
              onClick={() => handleLanguageSelect(Language.DE)}
              className="group relative w-full py-6 bg-yellow-600/20 hover:bg-yellow-600/40 border border-yellow-500/30 rounded-2xl font-black text-xl md:text-2xl transition-all active:scale-95 flex items-center justify-center gap-4"
            >
              <span className="text-2xl">🇩🇪</span>
              <span className="text-white tracking-widest">DEUTSCH</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (appMode === APP_MODES.LANDING) {
    return (
      <div className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden bg-[#020617] text-center p-6 py-12 md:py-20 select-none">
        <PortalBackground />
        
        <div className="z-10 relative flex flex-col items-center justify-center w-full mb-4">
           <div className="w-full max-w-[120px] md:max-w-[180px] mb-2">
             <img 
               src="https://m.media-amazon.com/images/I/61wgT+7R8AL._SL1500_.jpg" 
               alt="Gateway Process Logo" 
               className="w-full h-auto object-contain drop-shadow-[0_0_30px_rgba(255,255,255,0.2)]"
               referrerPolicy="no-referrer"
             />
           </div>
           <div className="flex items-center gap-2 mt-2">
              <span className="text-[9px] md:text-lg text-blue-400 font-mono-cia uppercase font-bold animate-pulse">{t.landing.status}</span>
           </div>
        </div>
        <div className="z-20 flex flex-col items-center gap-6 w-full max-sm:px-4">
          <div className="w-full flex flex-col items-center gap-2">
            <button onClick={() => handleInitialize()} className="group relative w-full py-7 md:py-9 bg-blue-600 rounded-2xl font-black text-xl md:text-3xl transition-all shadow-[0_0_60px_rgba(37,99,235,0.5)] active:scale-95 border border-white/30 hover:bg-blue-500">{t.landing.start}</button>
            <div className="flex items-center gap-2 opacity-60">
              <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest">{t.landing.headphones}</span>
            </div>
          </div>
          <div className="flex flex-col items-center gap-4 w-full">
            <a href={CIA_DOC_URL} target="_blank" rel="noopener noreferrer" className="w-full max-w-xs text-[12px] md:text-sm font-black text-red-400 uppercase tracking-[0.2em] bg-red-600/10 px-6 py-4 rounded-full border border-red-500/20 hover:bg-red-600/20 transition-all">{t.landing.docs}</a>
            
            <a 
              href="https://www.thegatewayprotocol.com/#strumentazione" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 w-full max-w-xs px-6 py-4 bg-purple-600/10 hover:bg-purple-600/20 border border-purple-500/20 rounded-full transition-all active:scale-95 group"
            >
              <span className="text-purple-500 group-hover:scale-110 transition-transform">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
              </span>
              <span className="text-[10px] md:text-[12px] font-black text-purple-500 uppercase tracking-[0.2em]">{t.landing.equipment}</span>
            </a>

            <div className="flex justify-center gap-4 mt-2">
              <button 
                onClick={() => handleInitialize(Language.IT)}
                className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full transition-all active:scale-95"
              >
                <span className="text-xl">🇮🇹</span>
                <span className="text-[10px] font-black text-white tracking-widest uppercase">ITALIANO</span>
              </button>
              <button 
                onClick={() => handleInitialize(Language.DE)}
                className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full transition-all active:scale-95"
              >
                <span className="text-xl">🇩🇪</span>
                <span className="text-[10px] font-black text-white tracking-widest uppercase">DEUTSCH</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative h-full w-full bg-[#020617] text-slate-100 flex flex-col transition-all duration-300 ${vibeActive ? 'vibration-active' : ''}`}>
      <PortalBackground />
      {isShifting && <div className="fixed inset-0 z-[100] pointer-events-none bg-white/5 backdrop-blur-[2px] animate-vibrational-shift" />}
      
      <header className="z-20 sticky top-0 p-2 pt-[calc(env(safe-area-inset-top)+0.5rem)] md:p-6 flex items-center justify-between glass border-b border-white/10 h-auto md:h-20">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 md:w-10 md:h-10 rounded bg-blue-600 flex items-center justify-center font-black text-sm md:text-lg italic">G</div>
          <div className="flex flex-col">
            <h2 className="font-black text-[10px] md:text-[12px] uppercase tracking-tighter text-white leading-none">{t.landing.headerTitle}</h2>
            <span className="text-[7px] md:text-[8px] text-blue-400 font-mono-cia font-black uppercase mt-1">{t.landing.headerStatus}: {t.landing.status}</span>
          </div>
        </div>
        <nav className="hidden md:flex items-center bg-black/40 rounded-xl p-1 border border-white/10">
          {[{id:'tools',label:t.tabs.tools},{id:'session',label:t.tabs.session}].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id as 'tools' | 'session')} className={`px-6 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab.id ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'} ${tab.id === 'tools' && activeTab !== 'tools' ? 'animate-pulse text-yellow-500/80' : ''}`}>{tab.label}</button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-black/60 px-3 py-2 rounded-lg border border-white/5">
             <span className="text-[10px] font-black opacity-30">VOL</span>
             <input type="range" min="0" max="1" step="0.01" value={volume} onChange={(e) => {setVolume(parseFloat(e.target.value)); audioEngine.setVolume(parseFloat(e.target.value));}} className="w-12 md:w-20 accent-blue-500" />
          </div>
        </div>
      </header>

      <main className="z-10 flex-grow p-2 md:p-4 max-w-7xl mx-auto w-full flex flex-col gap-2 md:gap-4 overflow-y-auto custom-scrollbar">
        {activeTab === 'tools' && (
          <div className="animate-fade-in flex flex-col gap-4">
            <div className="flex justify-center gap-2 mb-2">
              <button 
                onClick={() => setActiveTool('box')}
                className={`px-4 py-2 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all ${activeTool === 'box' ? 'bg-blue-600 text-white' : 'bg-white/5 text-slate-500'}`}
              >
                📦 {t.tools.box}
              </button>
              <button 
                onClick={() => setActiveTool('rebal')}
                className={`px-4 py-2 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all ${activeTool === 'rebal' ? 'bg-purple-600 text-white' : 'bg-white/5 text-slate-500'}`}
              >
                🛡️ {t.tools.shield}
              </button>
            </div>
            {activeTool === 'box' ? (
              <EnergyConversionBox onProceed={() => setActiveTool('rebal')} language={language} />
            ) : (
              <ResonantEnergyBalloon onProceed={() => setActiveTab('session')} language={language} />
            )}
          </div>
        )}

        {activeTab === 'session' && (
          <div className="flex flex-col gap-2 md:gap-4 w-full animate-fade-in">
            <div className="glass rounded-[1.5rem] md:rounded-[3rem] p-3 md:p-10 flex flex-col items-center justify-center min-h-[240px] md:min-h-[600px] relative overflow-hidden border border-white/10 bg-black/20 shadow-2xl">
               <div className="w-full text-center z-10">
                 <div className="flex flex-col items-center">
                   <div className="flex items-center gap-2 md:gap-4 mb-1 md:mb-2">
                     <div className="h-[1px] w-6 md:w-20 bg-gradient-to-r from-transparent to-blue-500/30"></div>
                     <span className="text-[7px] md:text-[12px] font-mono-cia text-blue-400 uppercase tracking-[0.4em] md:tracking-[0.8em] font-black">{t.monitor.title}</span>
                     <div className="h-[1px] w-6 md:w-20 bg-gradient-to-l from-transparent to-blue-500/30"></div>
                   </div>
                   
                   <div className="relative py-4 md:py-12 flex items-center justify-center w-full">
                     <HemiSyncAnimation 
                        language={language}
                        isActive={isSessionActive}
                       frequency={currentConfig?.binauralFreq || 4}
                       color={currentConfig?.color || '#3b82f6'}
                       label={activeFocus.replace('FOCUS ', 'F-')}
                       onInstructionsClick={() => {
                         if (currentConfig) {
                           setInstructionFocus(currentConfig);
                           setIsInstructionsOpen(true);
                         }
                       }}
                     />
                   </div>

                   <div className="flex flex-wrap justify-center items-center gap-4 md:gap-12 bg-black/60 px-4 md:px-8 py-3 md:py-6 rounded-[1.5rem] md:rounded-[2rem] border border-white/10 backdrop-blur-2xl shadow-[0_0:50px_rgba(0,0,0,0.5)]">
                     <div className="flex flex-col items-center">
                       <span className="text-[6px] md:text-[8px] font-mono-cia text-slate-500 uppercase tracking-widest mb-0.5">{t.monitor.left}</span>
                       <span className="text-[10px] md:text-[20px] font-mono-cia text-white font-black">{currentConfig?.baseFreq} HZ</span>
                     </div>
                     <div className="hidden md:block w-[1px] h-10 bg-white/10"></div>
                     <div className="flex flex-col items-center">
                       <span className="text-[6px] md:text-[8px] font-mono-cia text-slate-500 uppercase tracking-widest mb-0.5">{t.monitor.right}</span>
                       <span className="text-[10px] md:text-[20px] font-mono-cia text-white font-black">{(currentConfig?.baseFreq || 0) + (currentConfig?.binauralFreq || 0)} HZ</span>
                     </div>
                     <div className="hidden md:block w-[1px] h-10 bg-white/10"></div>
                     <div className="flex flex-col items-center">
                       <span className="text-[6px] md:text-[8px] font-mono-cia text-blue-400 uppercase tracking-widest mb-0.5">{t.monitor.diff}</span>
                       <span className="text-[10px] md:text-[20px] font-mono-cia text-blue-400 font-black">{currentConfig?.binauralFreq} HZ</span>
                     </div>
                   </div>
                 </div>
               </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SessionTimer timeLeft={timeLeft} isActive={isSessionActive} totalDuration={totalDuration} onToggle={toggleSession} onSetDuration={(m) => {setTimeLeft(m*60); setTotalDuration(m*60);}} isAutoPilot={isAutoProtocol} onAutoPilotToggle={handleAutoPilotToggle} language={language} />
              
              <div className="glass p-4 md:p-6 rounded-[2rem] md:rounded-[2.5rem] border border-white/10 shadow-2xl relative flex flex-col">
                <div className="text-center mb-2 md:mb-6">
                   <h4 className="text-[7px] md:text-[10px] font-mono-cia text-blue-400 uppercase tracking-[0.3em] md:tracking-[0.5em] font-black">{t.monitor.activation}</h4>
                </div>
                
                <div className="grid grid-cols-1 gap-2 md:gap-3 flex-grow">
                  {localizedConfigs.map((config) => {
                    const isActive = activeFocus === config.id;
                    const isUnlocked = unlockedLevels.includes(config.id);
                    const color = config.color || '#3b82f6';
                    const freq = config.binauralFreq;
                    const subtitle = config.name.split(': ')[1];
                    const animationDuration = `${Math.max(0.5, 3 / freq)}s`;
                    
                    return (
                      <div 
                        key={config.id} 
                        onClick={() => handleFocusChange(config)} 
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleFocusChange(config);
                          }
                        }}
                        role="button"
                        tabIndex={isUnlocked ? 0 : -1}
                        className={`group relative flex flex-col p-3 md:p-6 rounded-[1.5rem] md:rounded-[2rem] border transition-all duration-500 active:scale-95 overflow-hidden cursor-pointer ${
                          isActive ? 'bg-black/90 border-white/30 shadow-[0_0_40px_rgba(255,255,255,0.05)]' : 
                          isUnlocked ? 'bg-white/[0.03] border-white/5 hover:bg-white/[0.08]' : 
                          'bg-black/40 border-white/5 opacity-60 grayscale cursor-not-allowed'
                        }`}
                        style={{ 
                          borderColor: isActive ? `${color}80` : undefined,
                          boxShadow: isActive ? `0 0 30px ${color}20` : 'none'
                        }}
                      >
                        {!isUnlocked && (
                          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 backdrop-blur-[1px]">
                            <div className="flex flex-col items-center gap-2">
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                              <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Locked</span>
                            </div>
                          </div>
                        )}
                        <div className="flex justify-between items-start relative z-10 w-full mb-1 md:mb-2">
                          <div className="flex flex-col text-left">
                            <span className={`text-[14px] md:text-[22px] font-black italic uppercase tracking-tighter leading-none transition-colors duration-500 ${isActive ? 'text-white' : 'text-slate-500'}`}>
                              {config.id}
                            </span>
                            <span className={`text-[8px] md:text-[10px] font-black uppercase tracking-widest mt-0.5 transition-colors duration-500 ${isActive ? 'text-blue-400' : 'text-slate-700'}`}>
                              {subtitle}
                            </span>
                            <span className={`text-[7px] font-black uppercase tracking-[0.2em] mt-1 transition-colors duration-500 ${isActive ? 'text-white/40' : 'text-slate-800'}`}>
                              {config.brainwaveType} • {config.binauralFreq}Hz
                            </span>
                          </div>
                          <div className="flex flex-col items-end">
                             <span className={`text-[14px] md:text-[32px] font-mono-cia font-black tracking-tighter uppercase transition-colors duration-500 ${isActive ? 'text-white' : isUnlocked ? 'text-slate-400' : 'text-slate-600'}`}>
                               {config.durationLabel}
                             </span>
                          </div>
                        </div>

                        {!isActive && (
                          <div className="mt-4 w-full relative z-10">
                            <div className="h-[1px] w-full bg-white/[0.05]" />
                          </div>
                        )}

                        {/* Frequency-Synced Dynamic Brainwave Schematics */}
                        <div className="h-6 md:h-12 w-full mt-2 md:mt-4 relative z-10 overflow-hidden">
                          <svg viewBox="0 0 100 24" preserveAspectRatio="none" className="w-full h-full">
                            <defs>
                              <linearGradient id={`grad-${config.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor={color} stopOpacity="0.1" />
                                <stop offset="50%" stopColor={color} stopOpacity="0.8" />
                                <stop offset="100%" stopColor={color} stopOpacity="0.1" />
                              </linearGradient>
                            </defs>
                            
                            {/* Static/Inactive Wave */}
                            {!isActive && (
                              <path 
                                d={generateContinuousWavePath(freq)} 
                                fill="none" 
                                stroke="#1e293b" 
                                strokeWidth="1"
                                strokeDasharray="2, 2"
                              />
                            )}

                            {/* Active Dynamic Waves (Dual Layer Interference) */}
                            {isActive && (
                              <g>
                                <animateTransform 
                                  attributeName="transform" 
                                  type="translate" 
                                  from="-100 0" 
                                  to="0 0" 
                                  dur={animationDuration} 
                                  repeatCount="indefinite" 
                                />
                                <path 
                                  d={generateContinuousWavePath(freq, 0)} 
                                  fill="none" 
                                  stroke={`url(#grad-${config.id})`}
                                  strokeWidth="2.5"
                                />
                                <path 
                                  d={generateContinuousWavePath(freq, Math.PI / 2)} 
                                  fill="none" 
                                  stroke={color}
                                  strokeWidth="0.8"
                                  className="opacity-30"
                                />
                              </g>
                            )}
                          </svg>
                        </div>



                        {/* Progress Bar to Unlock Next Level */}
                        {isActive && isSessionActive && (
                          <div className="mt-4 w-full h-1.5 bg-white/5 rounded-full overflow-hidden relative z-10">
                            <div 
                              className="h-full transition-all duration-1000 ease-linear"
                              style={{ 
                                width: `${((totalDuration - timeLeft) / totalDuration) * 100}%`,
                                backgroundColor: color,
                                boxShadow: `0 0 10px ${color}`
                              }}
                            />
                          </div>
                        )}
                        
                        {isActive && (
                           <div className="absolute bottom-2 right-4 flex items-center gap-2">
                              <span className="text-[7px] font-black text-blue-400/60 tracking-[0.3em] font-mono-cia uppercase animate-pulse">PHASE SYNC LOCKED</span>
                              <div className="flex gap-1">
                                <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                                <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                              </div>
                           </div>
                        )}
                      </div>
                    );
                  })}

                  {/* OBE Button under Focus 21 */}
                  {unlockedLevels.includes(FocusState.FOCUS_21) && (
                      <button 
                        onClick={() => setIsOBEModalOpen(true)} 
                        className={`mt-2 w-full py-4 rounded-[1.5rem] md:rounded-[2rem] border transition-all duration-500 active:scale-95 flex flex-col items-center justify-center gap-1 ${
                          vibeActive 
                            ? 'bg-red-600/20 border-red-500 shadow-[0_0_30px_rgba(239,68,68,0.2)]' 
                            : 'bg-red-600/5 border-red-500/20 hover:bg-red-600/10'
                        }`}
                      >
                        <span className="text-[10px] font-black text-red-500 uppercase tracking-[0.3em]">{t.obe.title}</span>
                        <span className="text-[8px] font-mono-cia text-red-400/60 uppercase">{t.obe.subtitle}</span>
                      </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-[100] glass border-t border-white/10 flex justify-around p-1 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] md:hidden">
        {[{id:'tools',label:t.tabs.tools.split('. ')[1],icon:'🛠️'},{id:'session',label:t.tabs.session.split('. ')[1],icon:'🌀'}].map(tab => (
          <button 
            key={tab.id} 
            onClick={() => {
              hapticFeedback('light');
              setActiveTab(tab.id as 'tools' | 'session');
            }} 
            className={`flex flex-col items-center gap-0.5 p-2 rounded-xl transition-all w-20 ${activeTab === tab.id ? 'text-blue-400 bg-blue-500/10' : 'text-slate-500'} ${tab.id === 'tools' && activeTab !== 'tools' ? 'animate-pulse text-yellow-500/80' : ''}`}
          >
            <span className="text-2xl">{tab.icon}</span>
            <span className="text-[9px] font-black uppercase tracking-tighter">{tab.label}</span>
          </button>
        ))}
      </nav>

      {isOBEModalOpen && (
        <div className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-3xl p-4 flex items-center justify-center">
          <div className="max-w-md w-full glass border-red-500/30 p-8 rounded-[2.5rem] relative">
            <button onClick={() => { stopVibration(); setIsOBEModalOpen(false); }} className="absolute top-4 right-6 text-3xl opacity-30">&times;</button>
            <h2 className="text-2xl font-black text-red-500 uppercase text-center mb-6 italic tracking-tighter underline underline-offset-8">{t.obe.title}</h2>
            {vibeActive ? (
              <div className="mb-8 text-center animate-pulse">
                <div className="text-6xl font-mono-cia font-black text-red-500 mb-2">{Math.floor(vibeTimeLeft / 60)}:{(vibeTimeLeft % 60).toString().padStart(2, '0')}</div>
                <div className="text-[10px] font-black text-red-400/50 uppercase tracking-[0.5em]">{t.obe.resonanceActive}</div>
              </div>
            ) : (
              <div className="mb-6 space-y-4">
                 <p className="text-xs text-slate-400 italic text-center">{t.obe.instruction}</p>
                 <div className="flex justify-center gap-2">
                    {[60, 120, 300].map(v => (
                      <button key={v} onClick={() => setVibeDurationSetting(v)} className={`px-4 py-3 rounded-xl text-[10px] font-black transition-all ${vibeDurationSetting === v ? 'bg-red-600 text-white' : 'bg-white/5 text-slate-500 border border-white/5'}`}>{v/60}M</button>
                    ))}
                 </div>
              </div>
            )}
             <button onClick={handleVibrationToggle} className={`w-full py-5 rounded-2xl font-black text-sm transition-all active:scale-95 ${vibeActive ? 'bg-red-700 text-white animate-pulse' : 'bg-red-600/10 border border-red-600/50 text-red-500 hover:bg-red-600/30'}`}>
               {vibeActive ? t.obe.terminate : t.obe.initiate}
             </button>

             <div className="mt-8 space-y-6 overflow-y-auto max-h-[40vh] pr-2 custom-scrollbar">
                <div className="bg-red-500/5 border border-red-500/20 p-4 rounded-2xl">
                  <p className="text-[10px] text-slate-300 leading-relaxed font-medium italic">
                    "{t.obe.intro}"
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="text-[10px] font-mono-cia text-red-400 uppercase tracking-widest font-black">{t.obe.vibrationalTitle}</h4>
                  <p className="text-[9px] text-slate-400 leading-relaxed">
                    {t.obe.vibrationalContent}
                  </p>
                </div>

                <div className="space-y-3">
                  {t.obe.protocols.map((protocol: { name: string; description: string }, idx: number) => (
                    <div key={idx} className="bg-white/5 p-3 rounded-xl border border-white/5">
                      <h5 className="text-[9px] font-mono-cia text-white uppercase mb-1 font-black">{protocol.name}</h5>
                      <p className="text-[8px] text-slate-500 leading-relaxed">{protocol.description}</p>
                    </div>
                  ))}
                </div>

                <div className="text-center pt-4 border-t border-white/5">
                  <p className="text-[8px] font-mono-cia text-slate-500 uppercase tracking-widest opacity-60">
                    {t.obe.closing}
                  </p>
                </div>
             </div>
           </div>
         </div>
       )}
      
      <style>{`
        @keyframes fade-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
      `}</style>
      {isInstructionsOpen && instructionFocus && (
        <div className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-3xl p-4 flex items-center justify-center">
          <div className="max-w-2xl w-full glass border-white/10 rounded-[2.5rem] relative flex flex-col max-h-[90vh] overflow-hidden">
            <button 
              onClick={() => {
                setIsInstructionsOpen(false);
                if (activeGuideRef.current) {
                  activeGuideRef.current.stop();
                  activeGuideRef.current = null;
                  audioEngine.duckBackground(false);
                }
              }} 
              className="absolute top-6 right-8 text-3xl opacity-30 hover:opacity-100 transition-opacity z-10"
            >
              &times;
            </button>
            
            <div className="p-8 border-b border-white/10 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-black uppercase italic tracking-tighter" style={{ color: instructionFocus.color }}>{instructionFocus.id}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-[10px] font-mono-cia text-slate-500 uppercase tracking-widest">{t.briefing.title}</p>
                  <span className="w-1 h-1 bg-slate-700 rounded-full" />
                  <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em]">{instructionFocus.brainwaveType} RESONANCE ({instructionFocus.binauralFreq}Hz)</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 p-4 flex justify-center border-b border-white/10 gap-2">
              <button onClick={() => setManualTab('info')} className={`px-8 py-3 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${manualTab === 'info' ? 'bg-blue-600 text-white' : 'text-slate-500'}`}>{t.briefing.sensations}</button>
              <button onClick={() => setManualTab('tutorial')} className={`px-8 py-3 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${manualTab === 'tutorial' ? 'bg-blue-600 text-white' : 'text-slate-500'}`}>{t.briefing.methodology}</button>
            </div>

            <div className="p-8 flex-grow overflow-y-auto custom-scrollbar font-mono-cia text-slate-400 space-y-6">
              {manualTab === 'info' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {instructionFocus.description.split('###').slice(1).filter((_, idx) => idx === 0).map(s => 
                    s.replace(/.*EXPERIENCE|.*ESPERIENZA/, '').trim().split('\n').filter(l => l.includes('•')).map((line, j) => {
                      const [l, c] = line.replace('•', '').replace(/\*\*/g, '').split(':');
                      return (
                        <div key={j} className="p-5 bg-white/5 rounded-2xl border border-white/5 relative group/card">
                          <div className="flex justify-between items-start mb-1">
                            <div className="text-[10px] font-black text-white uppercase tracking-widest">{l?.trim()}</div>
                          </div>
                          <div className="text-[12px] leading-relaxed text-slate-500">{c?.trim()}</div>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <div className="max-w-2xl mx-auto space-y-4">
                  {instructionFocus.description.split('###').slice(1).filter((_, idx) => idx === 1).map(s => 
                    s.replace(/TUTORIAL: .*?/, '').trim().split('\n').filter(line => /^\d+\./.test(line.trim())).map((line, stepIdx) => {
                      const [l, content] = line.replace(/^\d+\./, '').replace(/\*\*/g, '').trim().split(':');
                      return (
                        <div key={stepIdx} className="flex gap-4 p-5 bg-white/5 rounded-2xl border border-white/5 group/step">
                          <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 font-black text-xs shrink-0">{stepIdx + 1}</div>
                          <div className="flex-grow">
                            <div className="flex justify-between items-start mb-1">
                              <div className="text-[11px] font-black text-white uppercase tracking-widest">{l}</div>
                            </div>
                            <div className="text-[13px] text-slate-500">{content}</div>
                            
                            {/* Inject interactive buttons for Focus 10 preparation steps */}
                            {instructionFocus.id === FocusState.FOCUS_10 && (
                              <div className="mt-3">
                                {(l.includes('ENERGY CONVERSION BOX') || l.includes('SCATOLA DI CONVERSIONE ENERGETICA') || l.includes('ENERGIE-UMWANDLUNGS-BOX')) && (
                                  <button 
                                    onClick={() => {
                                      setActiveTab('tools');
                                      setActiveTool('box');
                                      setIsInstructionsOpen(false);
                                    }}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[9px] font-black uppercase tracking-widest transition-all flex items-center gap-2 shadow-lg shadow-blue-900/20"
                                  >
                                    📦 {t.tools.box}
                                  </button>
                                )}
                                {(l.includes('RESONANT ENERGY BALLOON') || l.includes('PALLONE DI ENERGIA RISONANTE') || l.includes('RESONANZ-ENERGIE-BALLON')) && (
                                  <button 
                                    onClick={() => {
                                      setActiveTab('tools');
                                      setActiveTool('rebal');
                                      setIsInstructionsOpen(false);
                                    }}
                                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-[9px] font-black uppercase tracking-widest transition-all flex items-center gap-2 shadow-lg shadow-purple-900/20"
                                  >
                                    🛡️ {t.tools.shield}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <UpgradeModal isOpen={isUpgradeOpen} onClose={() => setIsUpgradeOpen(false)} />
    </div>
  );
};

export default App;
